import { useLocation } from 'contexts/LocationContext';
import { Location, TotalProductsType } from 'types';

type TotalProductsProps = {
  totalProducts: TotalProductsType[];
};

type ProductSubProps = {
  name: string;
  total: number;
  highestNumSettled: number;
  type: 'product' | 'location';
  isCeasedProduct: boolean;
};

type ProductProps = {
  index: number;
  product: TotalProductsType;
  location: Location | null;
  highestNumSettled: number;
};

function TotalProductRow({
  index,
  product,
  location,
  highestNumSettled
}: ProductProps) {
  const isCeasedProduct = CEASED_PRODUCTS.some(
    (_name) => _name === product.name
  );

  return (
    <li
      key={product.name}
      className={`flex flex-col w-full p-4 border-b border-gray-200 ${
        index % 2 !== 0 ? 'bg-grayMuted' : ''
      }`}
      aria-roledescription="listitem"
    >
      <TotalProductSubRow
        name={product.name}
        total={product.total}
        highestNumSettled={highestNumSettled}
        isCeasedProduct={isCeasedProduct}
        type="product"
      />
      {location && (
        <TotalProductSubRow
          name={`In ${location.location}`}
          total={Number(location[product.key])}
          highestNumSettled={highestNumSettled}
          isCeasedProduct={isCeasedProduct}
          type="location"
        />
      )}
      {isCeasedProduct && (
        <p
          className="flex-3 w-20 sm:w-48 text-right text-xs text-primary"
          aria-roledescription="cell"
        >
          *Bundle ended 30 Jun 2023
        </p>
      )}
    </li>
  );
}
function TotalProductSubRow({
  name,
  total,
  highestNumSettled,
  type,
  isCeasedProduct = false
}: ProductSubProps) {
  const dataIsAvailable = total > 0;
  const progressBarWidth = (total / highestNumSettled) * 100;
  const isProductType = type === 'product';

  return (
    <div className="flex flex-row w-full space-x-4">
      <div className="flex-3 w-40 sm:w-64 text-right">
        <p
          className={` ${isProductType ? 'font-bold' : ''}`}
          aria-roledescription="cell"
        >
          {isProductType && isCeasedProduct ? `${name}*` : name}
        </p>
      </div>

      {dataIsAvailable ? (
        <div className="flex-3 w-96 sm:w-full bg-gray-200 h-1.5 mt-2">
          <div
            className={`${isProductType ? 'bg-primary' : 'bg-black'} h-1.5`}
            style={{
              width: `${progressBarWidth}%`
            }}
          ></div>
        </div>
      ) : (
        <div className="flex-3 w-96 sm:w-full">
          <p className="absolute">
            For privacy protection, this number has been hidden
          </p>
        </div>
      )}
      <p
        className={`w-16 sm:w-48 md:w-40 text-end ml-2 ${
          dataIsAvailable ? 'flex-3' : 'flex-2'
        }`}
        aria-roledescription="cell"
      >
        {dataIsAvailable && (
          <>
            {total}
            <span className="hidden sm:inline-block">&nbsp;upgrades</span>
          </>
        )}
      </p>
    </div>
  );
}
export default function TotalProducts({ totalProducts }: TotalProductsProps) {
  const [location] = useLocation();

  return (
    <div id="total-products">
      <h2 className="h3">Total products by category</h2>

      <ul
        className="section w-full text-sm font-medium text-gray-900 bg-white border border-gray-200"
        aria-roledescription="list"
      >
        {totalProducts.map((product, i) => (
          <TotalProductRow
            key={product.name}
            index={i}
            product={product}
            location={location}
            highestNumSettled={totalProducts[0].total}
          />
        ))}
      </ul>
    </div>
  );
}

const CEASED_PRODUCTS = ['Solar & Battery System'];
